import {
  getChannelDisplayName,
  getChannelId,
  getChannelLastMessage,
  getChannelLastMessageTime,
  getChannelUnreadCount,
  getMessageText,
  isChannelPinned,
} from 'app/shared/components/TeamChat/TeamChat.utils';
import _ from 'lodash';

import { HEADER_CHAT_RECENT_LIMIT, HeaderChatSection } from './HeaderChatPopover.constants';

const matchesSearchQuery = (channel, normalizedQuery) => {
  if (!normalizedQuery) {
    return true;
  }

  const title = getChannelDisplayName(channel).toLowerCase();
  const lastMessage = getMessageText(getChannelLastMessage(channel)).toLowerCase();

  return title.includes(normalizedQuery) || lastMessage.includes(normalizedQuery);
};

export const filterChannelsForPopover = ({ channels, searchQuery }) => {
  if (_.isEmpty(channels)) {
    return [];
  }

  const normalizedQuery = (searchQuery ?? '').trim().toLowerCase();

  return channels.filter(channel => matchesSearchQuery(channel, normalizedQuery));
};

const getLastMessageEpoch = channel => {
  const time = getChannelLastMessageTime(channel);

  return time ? new Date(time).getTime() : 0;
};

const sortChannels = channels =>
  channels.slice().sort((a, b) => {
    const unreadA = getChannelUnreadCount(a);
    const unreadB = getChannelUnreadCount(b);

    if (unreadA !== unreadB) {
      return unreadB - unreadA;
    }

    return getLastMessageEpoch(b) - getLastMessageEpoch(a);
  });

const getChannelBucketKey = (channel, mentionChatIds) => {
  if (isChannelPinned(channel)) {
    return HeaderChatSection.PINNED;
  }

  const chatId = getChannelId(channel);
  const unread = getChannelUnreadCount(channel);

  if (unread > 0 && chatId && mentionChatIds.has(chatId)) {
    return HeaderChatSection.MENTIONED;
  }

  if (unread > 0) {
    return HeaderChatSection.UNREAD;
  }

  return HeaderChatSection.RECENT;
};

const CHANNEL_SECTION_LABELS = {
  [HeaderChatSection.PINNED]: 'Pinned chats',
  [HeaderChatSection.MENTIONED]: 'Mentions',
  [HeaderChatSection.UNREAD]: 'Unread',
  [HeaderChatSection.RECENT]: 'Recent',
};

const CHANNEL_SECTION_ORDER = [
  HeaderChatSection.PINNED,
  HeaderChatSection.MENTIONED,
  HeaderChatSection.UNREAD,
  HeaderChatSection.RECENT,
];

const getMentionChatIds = mentions => {
  if (!mentions?.length) {
    return new Set();
  }

  return new Set(mentions.map(mention => mention?.chatId).filter(Boolean));
};

export const groupChannelsForPopover = ({ channels, mentions = [] }) => {
  if (_.isEmpty(channels)) {
    return [];
  }

  const mentionChatIds = getMentionChatIds(mentions);

  const buckets = _.groupBy(channels, channel => getChannelBucketKey(channel, mentionChatIds));

  return CHANNEL_SECTION_ORDER.reduce((acc, key) => {
    const sorted = sortChannels(buckets[key] ?? []);

    if (_.isEmpty(sorted)) {
      return acc;
    }

    const isRecent = key === HeaderChatSection.RECENT;
    const items = isRecent ? sorted.slice(0, HEADER_CHAT_RECENT_LIMIT) : sorted;

    return [
      ...acc,
      {
        key,
        label: CHANNEL_SECTION_LABELS[key],
        items,
      },
    ];
  }, []);
};
